import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, Check, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';

type SkillLevel = 'beginner' | 'intermediate' | 'advanced';

interface LevelOption {
  key: SkillLevel;
  emoji: string;
  label: string;
  description: string;
}

const LEVELS: LevelOption[] = [
  { key: 'beginner',     emoji: '🌱', label: 'Beginner',     description: 'New to trading or just getting started' },
  { key: 'intermediate', emoji: '📈', label: 'Intermediate', description: "You've been trading for a while" },
  { key: 'advanced',     emoji: '🎯', label: 'Advanced',     description: 'You trade actively or professionally' },
];

interface Preset {
  key: string;
  label: string;
  pairs: string[];
}

const PRESETS: Preset[] = [
  { key: 'fx-day',     label: 'FX Day Trader',         pairs: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'XAU/USD'] },
  { key: 'fx-swing',   label: 'FX Swing',              pairs: ['AUD/USD', 'USD/CAD', 'USD/CHF', 'NZD/USD'] },
  { key: 'gbp-focus',  label: 'GBP-Focused',           pairs: ['GBP/USD', 'GBP/JPY', 'GBP/AUD', 'EUR/GBP'] },
  // Symbols match the canonical quote/bias names the rest of the app uses —
  // mismatches here (e.g. "SPX500" when the dashboard knows it as "S&P 500")
  // make the customise modal show only some picks as selected even though
  // pulsePairs holds 4, because the buttons render by canonical symbol.
  { key: 'index-mix',  label: 'Index Mix',             pairs: ['S&P 500', 'NASDAQ', 'DOW', 'XAU/USD'] },
  { key: 'commodities',label: 'Commodities',           pairs: ['XAU/USD', 'XAG/USD', 'Oil (WTI)', 'USD/CAD'] },
];

interface PairGroup {
  key: string;
  label: string;
  pairs: string[];
}

const PAIR_GROUPS: PairGroup[] = [
  // 7 + 21 + 3 + 3 = 34 pairs — mirrors what /api/market/quotes returns
  // so the Onboarding picker and the Customise Pulse modal show the
  // exact same set. Crypto deliberately not listed: the bias engine
  // produces unreliable reads on crypto (24h volatility is too high for
  // a daily-bias %-threshold to be meaningful).
  { key: 'majors',      label: 'Majors',      pairs: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'USD/CHF', 'AUD/USD', 'USD/CAD', 'NZD/USD'] },
  { key: 'crosses',     label: 'Crosses',     pairs: ['EUR/GBP', 'EUR/JPY', 'EUR/CHF', 'EUR/AUD', 'EUR/CAD', 'EUR/NZD', 'GBP/JPY', 'GBP/CHF', 'GBP/AUD', 'GBP/CAD', 'GBP/NZD', 'AUD/JPY', 'AUD/NZD', 'CAD/JPY', 'CHF/JPY', 'NZD/JPY', 'AUD/CAD', 'AUD/CHF', 'CAD/CHF', 'NZD/CAD', 'NZD/CHF'] },
  { key: 'commodities', label: 'Commodities', pairs: ['XAU/USD', 'XAG/USD', 'Oil (WTI)'] },
  { key: 'indices',     label: 'Indices',     pairs: ['S&P 500', 'NASDAQ', 'DOW'] },
];

const DEFAULT_PULSE = ['EUR/USD', 'GBP/USD', 'USD/JPY', 'XAU/USD'];

function ProgressDots({ step }: { step: 1 | 2 }) {
  return (
    <div className="flex items-center justify-center gap-2" aria-label={`Step ${step} of 2`}>
      {[1, 2].map((s) => (
        <motion.span
          key={s}
          initial={false}
          animate={{
            width: s === step ? 28 : 8,
            backgroundColor: s <= step ? 'rgb(var(--accent))' : 'rgb(var(--border))',
          }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="h-2 rounded-full"
        />
      ))}
    </div>
  );
}

function Logo() {
  return (
    <svg width="22" height="22" viewBox="8 8 60 60" fill="none" className="text-accent">
      <circle cx="38" cy="38" r="30" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.3" />
      <circle cx="38" cy="38" r="12" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="38" cy="38" r="4.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="38" cy="38" r="1.8" fill="currentColor" />
      <line x1="38" y1="8" x2="38" y2="24" stroke="currentColor" strokeWidth="1.2" />
      <line x1="38" y1="52" x2="38" y2="68" stroke="currentColor" strokeWidth="1.2" />
      <line x1="8" y1="38" x2="24" y2="38" stroke="currentColor" strokeWidth="1.2" />
      <line x1="52" y1="38" x2="68" y2="38" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function LevelCard({
  option,
  onSelect,
  index,
}: {
  option: LevelOption;
  onSelect: (k: SkillLevel) => void;
  index: number;
}) {
  return (
    <motion.button
      type="button"
      onClick={() => onSelect(option.key)}
      variants={{
        hidden: { opacity: 0, y: 10 },
        show:   { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } },
      }}
      custom={index}
      whileHover={{ scale: 1.015 }}
      whileTap={{ scale: 0.99 }}
      className={cn(
        'group w-full text-left',
        'flex items-center gap-4 p-5 rounded-xl',
        'bg-bg-card border border-border',
        'transition-colors duration-200 ease-out-expo',
        'hover:border-accent hover:bg-bg-elevated',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
      )}
    >
      <span className="text-2xl shrink-0 transition-transform duration-200 group-hover:scale-110" aria-hidden>
        {option.emoji}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-base font-semibold text-fg">{option.label}</span>
        <span className="block mt-0.5 text-xs text-fg-muted">{option.description}</span>
      </span>
      <span className="text-fg-subtle group-hover:text-accent transition-colors text-lg" aria-hidden>
        →
      </span>
    </motion.button>
  );
}

function PairChip({
  pair,
  selected,
  disabled,
  onToggle,
}: {
  pair: string;
  selected: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      variants={{
        hidden: { opacity: 0, y: 6 },
        show:   { opacity: 1, y: 0, transition: { duration: 0.2 } },
      }}
      whileTap={{ scale: 0.96 }}
      className={cn(
        'inline-flex items-center gap-1 h-7 px-2.5 rounded-full',
        'text-2xs font-semibold tabular',
        'transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
        selected
          ? 'bg-accent text-bg border border-accent shadow-glow'
          : 'bg-bg-elevated text-fg-muted border border-border hover:border-border-strong hover:text-fg',
        disabled && !selected && 'opacity-50 cursor-not-allowed',
      )}
    >
      {selected && <Check className="size-3" />}
      {pair}
    </motion.button>
  );
}

function PairGroupAccordion({
  group,
  selected,
  capReached,
  onToggle,
  defaultOpen,
}: {
  group: PairGroup;
  selected: string[];
  capReached: boolean;
  onToggle: (p: string) => void;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const count = useMemo(() => group.pairs.filter((p) => selected.includes(p)).length, [group.pairs, selected]);

  return (
    <div className="rounded-xl bg-bg-card border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-bg-elevated transition-colors"
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-fg">{group.label}</span>
        <span className="flex items-center gap-2">
          {count > 0 && (
            <span className="text-2xs font-semibold tabular text-accent">
              {count} picked
            </span>
          )}
          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} className="inline-flex">
            <ChevronDown className="size-4 text-fg-subtle" />
          </motion.span>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.02 } } }}
              className="px-4 pb-4 flex flex-wrap gap-2 border-t border-border/60 pt-3"
            >
              {group.pairs.map((p) => {
                const sel = selected.includes(p);
                return (
                  <PairChip
                    key={p}
                    pair={p}
                    selected={sel}
                    disabled={capReached && !sel}
                    onToggle={() => onToggle(p)}
                  />
                );
              })}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [skill, setSkill] = useState<SkillLevel | null>(null);
  const [pairs, setPairs] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const capReached = pairs.length >= 4;

  function pickLevel(k: SkillLevel) {
    setSkill(k);
    setStep(2);
  }

  function skipLevel() {
    setSkill('beginner');
    setStep(2);
  }

  function togglePair(p: string) {
    setPairs((cur) => {
      if (cur.includes(p)) return cur.filter((x) => x !== p);
      if (cur.length >= 4) return cur;
      return [...cur, p];
    });
  }

  function applyPreset(preset: Preset) {
    setPairs(preset.pairs.slice(0, 4));
  }

  async function submit(finalPairs: string[]) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.post('/api/preferences', {
        skillLevel: skill ?? 'beginner',
        pulsePairs: finalPairs,
        biasPairs: finalPairs,
        pairsPicked: true,
      });
      navigate('/');
    } catch (e: any) {
      setSubmitError(e?.message || "Couldn't save preferences. Try again.");
      setSubmitting(false);
    }
  }

  function applyDefaults() {
    void submit(DEFAULT_PULSE);
  }

  function finish() {
    if (pairs.length === 0) return applyDefaults();
    void submit(pairs);
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Header */}
      <header className="px-6 lg:px-12 py-5 flex items-center justify-between border-b border-border/40">
        <Link to="/" className="flex items-center gap-2">
          <Logo />
          <span className="font-bold text-base tracking-wide">TELOS</span>
        </Link>
        <ProgressDots step={step} />
        <span className="text-2xs text-fg-subtle tabular hidden sm:inline">
          Step {step} of 2
        </span>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-start sm:items-center justify-center px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-3xl">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.section
                key="step1"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="space-y-7"
              >
                <header className="text-center space-y-2">
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
                    What's your trading experience?
                  </h1>
                  <p className="text-sm text-fg-muted">
                    We'll calibrate the depth of analysis and onboarding tips to your level.
                  </p>
                </header>

                <motion.div
                  initial="hidden"
                  animate="show"
                  variants={{ show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } } }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-3"
                >
                  {LEVELS.map((opt, i) => (
                    <LevelCard key={opt.key} option={opt} onSelect={pickLevel} index={i} />
                  ))}
                </motion.div>

                <div className="flex items-center justify-center pt-2">
                  <Button variant="ghost" size="sm" onClick={skipLevel}>
                    Skip for now
                  </Button>
                </div>
              </motion.section>
            )}

            {step === 2 && (
              <motion.section
                key="step2"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="space-y-6"
              >
                <header className="text-center space-y-2">
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
                    Pick your top 4 pairs
                  </h1>
                  <p className="text-sm text-fg-muted">
                    These appear in your Market Pulse and Daily Bias on the dashboard.
                  </p>
                </header>

                {/* Counter */}
                <div className="flex items-center justify-center gap-2">
                  <Sparkles className={cn('size-4', capReached ? 'text-bull' : 'text-fg-subtle')} />
                  <span
                    className={cn(
                      'text-sm font-semibold tabular transition-colors',
                      capReached ? 'text-bull' : 'text-fg-muted',
                    )}
                  >
                    {pairs.length} of 4 selected
                  </span>
                </div>

                {/* Presets */}
                <div className="space-y-2">
                  <div className="text-2xs font-semibold uppercase tracking-wide text-fg-subtle px-1">
                    Quick start
                  </div>
                  <motion.div
                    initial="hidden"
                    animate="show"
                    variants={{ show: { transition: { staggerChildren: 0.04 } } }}
                    className="flex flex-wrap gap-2"
                  >
                    {PRESETS.map((p) => {
                      const active = p.pairs.every((x) => pairs.includes(x)) && pairs.length === p.pairs.length;
                      return (
                        <motion.button
                          key={p.key}
                          type="button"
                          onClick={() => applyPreset(p)}
                          variants={{
                            hidden: { opacity: 0, y: 6 },
                            show:   { opacity: 1, y: 0, transition: { duration: 0.25 } },
                          }}
                          whileTap={{ scale: 0.97 }}
                          className={cn(
                            'h-9 px-3 rounded-full text-xs font-semibold whitespace-nowrap',
                            'transition-colors duration-200 ease-out-expo',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
                            active
                              ? 'bg-accent text-bg border border-accent'
                              : 'bg-bg-card text-fg-muted border border-border hover:bg-bg-elevated hover:text-fg',
                          )}
                        >
                          {p.label}
                        </motion.button>
                      );
                    })}
                  </motion.div>
                </div>

                {/* Accordion groups */}
                <motion.div
                  initial="hidden"
                  animate="show"
                  variants={{ show: { transition: { staggerChildren: 0.04 } } }}
                  className="space-y-2"
                >
                  {PAIR_GROUPS.map((g, idx) => (
                    <motion.div
                      key={g.key}
                      variants={{
                        hidden: { opacity: 0, y: 6 },
                        show:   { opacity: 1, y: 0, transition: { duration: 0.25 } },
                      }}
                    >
                      <PairGroupAccordion
                        group={g}
                        selected={pairs}
                        capReached={capReached}
                        onToggle={togglePair}
                        defaultOpen={idx === 0}
                      />
                    </motion.div>
                  ))}
                </motion.div>

                {submitError && (
                  <div className="text-xs text-bear text-center" role="alert">
                    {submitError}
                  </div>
                )}

                {/* Footer actions */}
                <div className="flex items-center justify-between gap-3 pt-2">
                  <Button variant="ghost" size="md" onClick={() => setStep(1)} disabled={submitting}>
                    <ChevronLeft className="size-4" />
                    Back
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" size="md" onClick={applyDefaults} disabled={submitting}>
                      Use defaults
                    </Button>
                    <Button
                      variant="primary"
                      size="md"
                      onClick={finish}
                      loading={submitting}
                      disabled={submitting || pairs.length === 0}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
